import { defineMessages } from "react-intl";

import FieldFeature from "@mapmycustomers/shared/enum/fieldModel/FieldFeature";
import FieldType from "@mapmycustomers/shared/enum/fieldModel/FieldType";
import FilterOperator from "@mapmycustomers/shared/enum/FilterOperator";
import { Deal, EntityType } from "@mapmycustomers/shared/types/entity";

import i18nService from "@app/config/I18nService";
import localSettings from "@app/config/LocalSettings";
import CountryField from "@app/util/fieldModel/impl/CountryField";
import { filesFormatter, getFilesCsvFormatter } from "@app/util/fieldModel/impl/fieldUtil";
import TeamField from "@app/util/fieldModel/impl/TeamField";
import TerritoryField from "@app/util/fieldModel/impl/TerritoryField";
import { parseApiDateWithUtcTz } from "@app/util/parsers";

import getEntityFilter from "../filters/EntityFilter";
import { csvNumberFormatter } from "../formatters";

import DealFieldName from "./DealFieldName";
import ClosingDateField from "./impl/ClosingDateField";
import DateField from "./impl/DateField";
import DateTimeField from "./impl/DateTimeField";
import FieldModel from "./impl/FieldModel";
import { createField } from "./impl/fieldModelUtil";
import FrequencyDaysLeft from "./impl/FrequencyDaysLeft";
import FrequencyIntervalField from "./impl/FrequencyIntervalField";
import FrequencyStatusField from "./impl/FrequencyStatusField";
import FunnelField from "./impl/FunnelField";
import GroupsField from "./impl/GroupsField";
import LossReasonField from "./impl/LossReasonField";
import MapLocationField from "./impl/MapLocationField";
import NameField from "./impl/NameField";
import NoContactField from "./impl/NoContactField";
import NotesField from "./impl/NotesField";
import RottingDaysOutField from "./impl/RottingDaysOutField";
import SourceField from "./impl/SourceField";
import StageField from "./impl/StageField";
import UniqueIdField from "./impl/UniqueIdField";
import UserField from "./impl/UserField";
import ViewAsField from "./impl/ViewAsField";

// DealFieldName now lives in its own module so it can be imported without pulling the whole
// field-model graph (which causes a circular dependency). Re-exported here for back-compat.
export { DealFieldName };

const displayNames = defineMessages<DealFieldName>({
  [DealFieldName.ADDRESS]: {
    id: "deal.field.deal.address",
    defaultMessage: "Deal Street",
    description: "Address field of deal",
  },
  [DealFieldName.ADJUSTED_AMOUNT]: {
    id: "deal.field.adjustedAmount",
    defaultMessage: "Adjusted Amount",
    description: "Adjusted Amount field of deal",
  },
  [DealFieldName.AMOUNT]: {
    id: "deal.field.amount",
    defaultMessage: "Amount",
    description: "Amount field of deal",
  },
  [DealFieldName.CITY]: {
    id: "deal.field.city",
    defaultMessage: "Deal City",
    description: "City field of deal",
  },
  [DealFieldName.CLOSING_DATE]: {
    id: "deal.field.closingDate",
    defaultMessage: "Closing Date",
    description: "Closing Date field of deal",
  },
  [DealFieldName.COMPANY]: {
    id: "deal.field.account",
    defaultMessage: "Company",
    description: "Company field of deal",
  },
  [DealFieldName.COMPANY_EMAIL]: {
    id: "deal.field.account.email",
    defaultMessage: "Company Email",
    description: "Company Email field of deal",
  },
  [DealFieldName.COMPANY_IMPORT_ID]: {
    id: "deal.field.account.importId",
    defaultMessage: "Company ID",
    description: "CompanyId field of activity",
  },
  [DealFieldName.COUNTRY]: {
    id: "deal.field.country",
    defaultMessage: "Deal Country",
    description: "Country field of deal",
  },
  [DealFieldName.CREATED_AT]: {
    id: "deal.field.createdAt",
    defaultMessage: "Date Created",
    description: "Created At field of deal",
  },
  [DealFieldName.CREATOR]: {
    id: "deal.field.creator",
    defaultMessage: "Creator",
    description: "Creator field of deal",
  },
  [DealFieldName.CREATOR_EMAIL]: {
    id: "deal.field.creatorEmail",
    defaultMessage: "Creator Email",
    description: "Creator Email field of deal",
  },
  [DealFieldName.CREATOR_NAME]: {
    id: "deal.field.creatorName",
    defaultMessage: "Creator Name",
    description: "Creator Name field of deal",
  },
  [DealFieldName.CRM_LINK]: {
    id: "deal.field.crmLink",
    defaultMessage: "CRM Link",
    description: "CRM Link field of deal",
  },
  [DealFieldName.DAYS_IN_STAGE]: {
    id: "deal.field.daysInStage",
    defaultMessage: "Days in Stage",
    description: "Days in Stage field of deal",
  },
  [DealFieldName.DAYS_LEFT_IN_FREQUENCY]: {
    id: "deal.field.daysLeftInFrequency",
    defaultMessage: "Days Left In Frequency",
    description: " Days Left In Frequency field of deal",
  },
  [DealFieldName.DEAL_LOSS_COMMENT]: {
    id: "deal.field.dealLossComment",
    defaultMessage: "Loss Comment",
    description: "Loss Comment field of deal",
  },
  [DealFieldName.DEAL_LOSS_REASON]: {
    id: "deal.field.dealLossReason",
    defaultMessage: "Loss Reason",
    description: "Loss Reason field of deal",
  },
  [DealFieldName.FILES]: {
    id: "deal.field.files",
    defaultMessage: "Files",
    description: "Files field of deal",
  },
  [DealFieldName.FREQUENCY_INTERVAL]: {
    id: "deal.field.frequencyInterval",
    defaultMessage: "Frequency Interval",
    description: "Frequency Interval field of deal",
  },
  [DealFieldName.FREQUENCY_STATUS]: {
    id: "deal.field.frequencyStatus",
    defaultMessage: "Frequency Status",
    description: "Frequency Status field of deal",
  },
  [DealFieldName.FUNNEL]: {
    id: "deal.field.funnel",
    defaultMessage: "Funnel",
    description: "Funnel field of deal",
  },
  [DealFieldName.GEOPOINT]: {
    id: "deal.field.geoPoint",
    defaultMessage: "Location",
    description: "Location field of deal",
  },
  [DealFieldName.GROUPS]: {
    id: "deal.field.groups",
    defaultMessage: "Group",
    description: "Groups field of deal",
  },
  [DealFieldName.LAST_ACTIVITY_DATE]: {
    id: "deal.field.lastActivity.completedAt",
    defaultMessage: "Date of Last Done Activity",
    description: "Date of last done activity field of deal",
  },
  [DealFieldName.LAST_ACTIVITY_NAME]: {
    id: "deal.field.lastActivity.name",
    defaultMessage: "Name of Last Done Activity",
    description: "Name of Last Done Activity field of deal",
  },
  [DealFieldName.LAST_ACTIVITY_TYPE]: {
    id: "deal.field.lastActivity.activityType",
    defaultMessage: "Type of Last Done Activity",
    description: "Type of Last Done Activity field of deal",
  },
  [DealFieldName.NAME]: {
    id: "deal.field.name",
    defaultMessage: "Name",
    description: "Name field of deal",
  },
  [DealFieldName.NEXT_ACTIVITY]: {
    id: "deal.field.nextActivity",
    defaultMessage: "Date of Next Activity",
    description: "Next Activity field of deal",
  },
  [DealFieldName.NO_CONTACT_IN]: {
    id: "deal.field.noContactIn",
    defaultMessage: "No Contact In",
    description: "No Contact In field of deal",
  },
  [DealFieldName.NOTES]: {
    id: "deal.field.notes",
    defaultMessage: "Notes",
    description: "Notes field of deal",
  },
  [DealFieldName.OWNER_EMAIL]: {
    id: "deal.field.ownerEmail",
    defaultMessage: "Owner Email",
    description: "Owner Email field of deal",
  },
  [DealFieldName.OWNER_NAME]: {
    id: "deal.field.ownerName",
    defaultMessage: "Owner Name",
    description: "Owner Name field of deal",
  },
  [DealFieldName.PERSON]: {
    id: "deal.field.contact",
    defaultMessage: "Person",
    description: "Person field of deal",
  },
  [DealFieldName.PERSON_EMAIL]: {
    id: "deal.field.contact.email",
    defaultMessage: "Person Email",
    description: "Person Email field of deal",
  },
  [DealFieldName.PERSON_IMPORT_ID]: {
    id: "deal.field.person.importId",
    defaultMessage: "Person ID",
    description: "PersonId field of activity",
  },
  [DealFieldName.POSTAL_CODE]: {
    id: "deal.field.postalCode",
    defaultMessage: "Deal Postal Code",
    description: "Postal Code field of deal",
  },
  [DealFieldName.QUICK_FILTER]: {
    id: "deal.field.quickFilter",
    defaultMessage: "",
    description: "Quick filter field of a deal",
  },
  [DealFieldName.REGION]: {
    id: "deal.field.region",
    defaultMessage: "Deal State/Region",
    description: "Region field of deal",
  },
  [DealFieldName.ROTTING_DAYS_OUT]: {
    id: "deal.field.rottingDaysOut",
    defaultMessage: "Days Rotting",
    description: "Days Rotting field of deal",
  },
  [DealFieldName.SCORE]: {
    id: "deal.field.score",
    defaultMessage: "Score",
    description: "Score field of deal",
  },
  [DealFieldName.SOURCE]: {
    id: "deal.field.source",
    defaultMessage: "Source",
    description: "Source field of deal",
  },
  [DealFieldName.STAGE]: {
    id: "deal.field.stage",
    defaultMessage: "Stage",
    description: "Stage field of deal",
  },
  [DealFieldName.STAGE_TYPE]: {
    id: "deal.field.stage.type",
    defaultMessage: "Stage Type",
    description: "Stage Type field of deal",
  },
  [DealFieldName.TEAM]: {
    id: "deal.field.team",
    defaultMessage: "Team",
    description: "Team field of deal",
  },
  [DealFieldName.TERRITORIES]: {
    id: "deal.field.territories",
    defaultMessage: "Territories",
    description: "Territories field of deal",
  },
  [DealFieldName.UNIQUE_ID]: {
    id: "deal.field.uniqueId",
    defaultMessage: "Unique ID",
    description: "Unique Id field of deal",
  },
  [DealFieldName.UPDATED_AT]: {
    id: "deal.field.updatedAt",
    defaultMessage: "Date Last Modified",
    description: "Modified At field of deal",
  },
  [DealFieldName.USER]: {
    id: "deal.field.user",
    defaultMessage: "Owner",
    description: "Owner field of deal",
  },
  [DealFieldName.VIEW_AS]: {
    id: "deal.field.viewAs",
    defaultMessage: "Visible to",
    description: "View As field of deal",
  },
});

const additionalDisplayNames = defineMessages({
  companyId: {
    id: "deal.field.account.importName",
    defaultMessage: "Company - ID",
    description: "Import name for the account field of deal",
  },
  personId: {
    id: "deal.field.contact.importName",
    defaultMessage: "Person - ID",
    description: "Import name for the contact field of deal",
  },
});

const dealFieldModel = new FieldModel(
  [
    new NameField({
      displayName: displayNames[DealFieldName.NAME],
      formProperties: { fullWidth: false },
      name: DealFieldName.NAME,
    }),
    {
      customGridProperties: {
        cellClass: ["cursor-pointer"],
      },
      displayName: displayNames[DealFieldName.SCORE],
      features: [
        FieldFeature.SORTABLE,
        FieldFeature.FILTERABLE,
        FieldFeature.FILTERABLE_ON_MAP,
        FieldFeature.BULK_EDIT,
        FieldFeature.HISTORY_FIELD,
        FieldFeature.NULLABLE,
        FieldFeature.NUMERIC,
      ],
      name: DealFieldName.SCORE,
      type: FieldType.NUMBER,
    },
    {
      customGridProperties: {
        cellRenderer: "crmLinkCellRenderer",
      },
      displayName: displayNames[DealFieldName.CRM_LINK],
      features: [
        FieldFeature.EXTERNAL_URL_FIELD,
        FieldFeature.FILTERABLE,
        FieldFeature.FILTERABLE_ON_MAP,
        FieldFeature.NON_IMPORT,
        FieldFeature.SORTABLE,
        FieldFeature.NON_INTEGRATION,
        FieldFeature.NON_ADDABLE_FORM_FIELD,
        FieldFeature.DETAILS,
      ],
      name: DealFieldName.CRM_LINK,
      type: FieldType.STRING,
    },
    {
      csvExportProperties: { valueFormatter: csvNumberFormatter },
      customGridProperties: {
        cellClass: ["cursor-pointer"],
      },
      displayName: displayNames[DealFieldName.AMOUNT],
      features: [
        FieldFeature.SORTABLE,
        FieldFeature.FILTERABLE,
        FieldFeature.FILTERABLE_ON_MAP,
        FieldFeature.VISIBLE_BY_DEFAULT,
        FieldFeature.BULK_EDIT,
        FieldFeature.NUMERIC,
        FieldFeature.DATA_VIEW,
        FieldFeature.MONETARY_VALUE,
        FieldFeature.HISTORY_FIELD,
      ],
      name: DealFieldName.AMOUNT,
      overridePhiSupport: false,
      type: FieldType.NUMBER,
      valueFormatter: (entity: unknown, value: unknown) =>
        i18nService.getIntl()?.formatNumber(value as number, {
          currency: localSettings.getCurrencyCode(),
          minimumFractionDigits: 0,
          style: "currency",
        }) ?? "",
    },
    {
      customGridProperties: {
        cellClass: ["cursor-pointer"],
      },
      displayName: displayNames[DealFieldName.ADJUSTED_AMOUNT],
      exportColumnName: "adjustedAmount",
      features: [
        FieldFeature.VISIBLE_BY_DEFAULT,
        FieldFeature.NUMERIC,
        FieldFeature.MONETARY_VALUE,
        FieldFeature.NON_IMPORT,
        FieldFeature.NON_INTEGRATION,
        FieldFeature.NON_MAP_VIEW,
        FieldFeature.NON_ADDABLE_FORM_FIELD,
      ],
      name: DealFieldName.ADJUSTED_AMOUNT,
      type: FieldType.NUMBER,
      valueFormatter: (entity: unknown, value: unknown) =>
        i18nService.getIntl()?.formatNumber(value as number, {
          currency: localSettings.getCurrencyCode(),
          minimumFractionDigits: 0,
          style: "currency",
        }) ?? "",
      valueGetter: (entity: unknown) => (entity as Deal).adjustedAmount ?? 0,
    },
    new SourceField({
      displayName: displayNames[DealFieldName.SOURCE],
      name: DealFieldName.SOURCE,
    }),
    new ClosingDateField({
      displayName: displayNames[DealFieldName.CLOSING_DATE],
      features: [
        FieldFeature.SORTABLE,
        FieldFeature.FILTERABLE,
        FieldFeature.FILTERABLE_ON_MAP,
        FieldFeature.VISIBLE_BY_DEFAULT,
        FieldFeature.BULK_EDIT,
        FieldFeature.HISTORY_FIELD,
      ],
      name: DealFieldName.CLOSING_DATE,
      overridePhiSupport: false,
      valueGetter: (entity: unknown) =>
        (entity as Deal).closingDate
          ? parseApiDateWithUtcTz((entity as Deal).closingDate as string)
          : null,
    }),
    {
      csvExportProperties: {
        valueFormatter: getFilesCsvFormatter(EntityType.DEAL),
      },
      displayName: displayNames[DealFieldName.FILES],
      features: [
        FieldFeature.FILE_FIELD,
        // not visible anywhere, except in forms and export
        FieldFeature.NON_IMPORT,
        FieldFeature.NON_INTEGRATION,
        FieldFeature.NON_LIST_VIEW,
        FieldFeature.NON_MAP_VIEW,
      ],
      formProperties: {
        fullWidth: true,
      },
      name: DealFieldName.FILES,
      type: FieldType.LIST,
      valueFormatter: filesFormatter,
    },
    new GroupsField({
      displayName: displayNames[DealFieldName.GROUPS],
      name: DealFieldName.GROUPS,
    }),
    new FrequencyIntervalField({
      displayName: displayNames[DealFieldName.FREQUENCY_INTERVAL],
      features: [
        FieldFeature.NON_INTEGRATION,
        FieldFeature.NON_IMPORT,
        FieldFeature.ENGAGEMENT,
        FieldFeature.BULK_EDIT,
        FieldFeature.FREQUENCY_INTERVAL,
        FieldFeature.FILTERABLE,
        FieldFeature.FILTERABLE_ON_MAP,
      ],
      name: DealFieldName.FREQUENCY_INTERVAL,
    }),
    new UserField({
      displayName: displayNames[DealFieldName.CREATOR],
      features: [
        FieldFeature.FILTERABLE,
        FieldFeature.FILTERABLE_ON_MAP,
        FieldFeature.SUPPORTS_SET_FILTER,
        FieldFeature.NON_EXPORT_VIEW,
        FieldFeature.NON_IMPORT,
        FieldFeature.NON_ADDABLE_FORM_FIELD,
        FieldFeature.DATA_VIEW,
      ],
      name: DealFieldName.CREATOR,
      platformFilterNames: ["createdBy"],
      platformName: "createdBy",
    }),
    new UserField({
      displayName: displayNames[DealFieldName.CREATOR_NAME],
      exportColumnName: "creator",
      features: [
        FieldFeature.FILTERABLE,
        FieldFeature.SUPPORTS_SET_FILTER,
        FieldFeature.NON_IMPORT,
        FieldFeature.NON_INTEGRATION,
        FieldFeature.NON_LIST_VIEW,
        FieldFeature.NON_ADDABLE_FORM_FIELD,
      ],
      filterName: "creator",
      name: DealFieldName.CREATOR_NAME,
      platformFilterNames: ["createdBy"],
      valueGetter: (entity: unknown) => (entity as Deal).creator,
    }),
    {
      displayName: displayNames[DealFieldName.CREATOR_EMAIL],
      features: [
        FieldFeature.NON_IMPORT,
        FieldFeature.NON_INTEGRATION,
        FieldFeature.NON_LIST_VIEW,
        FieldFeature.NON_ADDABLE_FORM_FIELD,
      ],
      name: DealFieldName.CREATOR_EMAIL,
      type: FieldType.STRING,
    },
    new UserField({
      displayName: displayNames[DealFieldName.USER],
      features: [
        FieldFeature.FILTERABLE,
        FieldFeature.FILTERABLE_ON_MAP,
        FieldFeature.SUPPORTS_SET_FILTER,
        FieldFeature.OWNER_FIELD,
        FieldFeature.VISIBLE_BY_DEFAULT,
        FieldFeature.NON_EXPORT_VIEW,
        FieldFeature.BULK_EDIT,
        FieldFeature.DATA_VIEW,
        FieldFeature.HISTORY_FIELD,
      ],
      importProperties: {
        required: false,
      },
      name: DealFieldName.USER,
      platformFilterNames: ["userId", "user.id"],
      platformName: "userId",
    }),
    new UserField({
      displayName: displayNames[DealFieldName.OWNER_NAME],
      exportColumnName: "user",
      features: [
        FieldFeature.FILTERABLE,
        FieldFeature.SUPPORTS_SET_FILTER,
        FieldFeature.OWNER_FIELD,
        FieldFeature.NON_IMPORT,
        FieldFeature.NON_INTEGRATION,
        FieldFeature.NON_LIST_VIEW,
        FieldFeature.NON_ADDABLE_FORM_FIELD,
      ],
      filterName: "user",
      name: DealFieldName.OWNER_NAME,
      platformFilterNames: ["userId", "user.id"],
      valueGetter: (entity: unknown) => (entity as Deal).user,
    }),
    {
      displayName: displayNames[DealFieldName.OWNER_EMAIL],
      features: [
        FieldFeature.NON_IMPORT,
        FieldFeature.NON_INTEGRATION,
        FieldFeature.NON_LIST_VIEW,
        FieldFeature.NON_ADDABLE_FORM_FIELD,
      ],
      name: DealFieldName.OWNER_EMAIL,
      type: FieldType.STRING,
    },
    new DateField({
      customGridProperties: {
        cellRenderer: "activityAnnotationLinkCellRenderer",
      },
      displayName: displayNames[DealFieldName.NEXT_ACTIVITY],
      features: [
        FieldFeature.SORTABLE,
        FieldFeature.FILTERABLE,
        FieldFeature.FILTERABLE_ON_MAP,
        FieldFeature.ENGAGEMENT,
        FieldFeature.NON_IMPORT,
        FieldFeature.NON_INTEGRATION,
        FieldFeature.NON_ADDABLE_FORM_FIELD,
      ],
      name: DealFieldName.NEXT_ACTIVITY,
    }),
    new DateField({
      customGridProperties: {
        cellRenderer: "activityAnnotationLinkCellRenderer",
      },
      displayName: displayNames[DealFieldName.LAST_ACTIVITY_DATE],
      features: [
        FieldFeature.SORTABLE,
        FieldFeature.FILTERABLE,
        FieldFeature.FILTERABLE_ON_MAP,
        FieldFeature.ENGAGEMENT,
        FieldFeature.NON_IMPORT,
        FieldFeature.NON_INTEGRATION,
        FieldFeature.NON_ADDABLE_FORM_FIELD,
      ],
      name: DealFieldName.LAST_ACTIVITY_DATE,
    }),
    {
      displayName: displayNames[DealFieldName.LAST_ACTIVITY_NAME],
      features: [
        FieldFeature.NON_ADDABLE_FORM_FIELD,
        FieldFeature.NON_IMPORT,
        FieldFeature.NON_INTEGRATION,
        FieldFeature.NON_MAP_VIEW,
      ],
      name: DealFieldName.LAST_ACTIVITY_NAME,
      type: FieldType.STRING,
    },
    {
      displayName: displayNames[DealFieldName.LAST_ACTIVITY_TYPE],
      features: [
        FieldFeature.NON_ADDABLE_FORM_FIELD,
        FieldFeature.NON_IMPORT,
        FieldFeature.NON_INTEGRATION,
        FieldFeature.NON_MAP_VIEW,
      ],
      name: DealFieldName.LAST_ACTIVITY_TYPE,
      type: FieldType.STRING,
    },
    new FrequencyStatusField({
      displayName: displayNames[DealFieldName.FREQUENCY_STATUS],
      name: DealFieldName.FREQUENCY_STATUS,
    }),
    new NoContactField({
      displayName: displayNames[DealFieldName.NO_CONTACT_IN],
      name: DealFieldName.NO_CONTACT_IN,
    }),
    new NotesField({
      displayName: displayNames[DealFieldName.NOTES],
      name: DealFieldName.NOTES,
    }),
    new UniqueIdField({
      displayName: displayNames[DealFieldName.UNIQUE_ID],
      name: DealFieldName.UNIQUE_ID,
    }),
    {
      customFilterConfig: {
        defaultInstance: "entity",
        filterInstances: {
          entity: getEntityFilter(EntityType.COMPANY, [
            FilterOperator.IN_ANY,
            FilterOperator.NONE_OF,
          ]),
        },
        operators: [FilterOperator.IN_ANY, FilterOperator.NONE_OF],
      },
      customGridProperties: {
        cellRenderer: "relationshipCellRenderer",
      },
      displayName: displayNames[DealFieldName.COMPANY],
      features: [
        FieldFeature.BULK_EDIT,
        FieldFeature.FILTERABLE_ON_MAP,
        FieldFeature.FILTERABLE,
        FieldFeature.HISTORY_FIELD,
        FieldFeature.RELATIONSHIPS_CATEGORY,
        FieldFeature.ASSOCIATED_ENTITY,
        FieldFeature.SUPPORTS_SET_FILTER,
        FieldFeature.VISIBLE_BY_DEFAULT,
      ],
      filterType: FieldType.ID,
      importProperties: {
        bottomPosition: true,
        displayName: additionalDisplayNames.companyId,
        required: false,
      },
      name: DealFieldName.COMPANY,
      platformFilterNames: ["account.id", "accountId"],
      platformName: "accountId",
      type: FieldType.STRING,
      valueGetter: ["account", "name"],
    },
    {
      customFilterConfig: {
        defaultInstance: "entity",
        filterInstances: {
          entity: getEntityFilter(EntityType.PERSON, [
            FilterOperator.IN_ANY,
            FilterOperator.NONE_OF,
          ]),
        },
        operators: [FilterOperator.IN_ANY, FilterOperator.NONE_OF],
      },
      customGridProperties: {
        cellRenderer: "relationshipCellRenderer",
      },
      displayName: displayNames[DealFieldName.PERSON],
      features: [
        FieldFeature.BULK_EDIT,
        FieldFeature.FILTERABLE_ON_MAP,
        FieldFeature.FILTERABLE,
        FieldFeature.HISTORY_FIELD,
        FieldFeature.RELATIONSHIPS_CATEGORY,
        FieldFeature.ASSOCIATED_ENTITY,
        FieldFeature.SUPPORTS_SET_FILTER,
        FieldFeature.VISIBLE_BY_DEFAULT,
      ],
      filterType: FieldType.ID,
      importProperties: {
        bottomPosition: true,
        displayName: additionalDisplayNames.personId,
        required: false,
      },
      name: DealFieldName.PERSON,
      platformFilterNames: ["contact.id", "contactId"],
      platformName: "contactId",
      type: FieldType.STRING,
      valueGetter: ["contact", "name"],
    },
    new MapLocationField({
      displayName: displayNames[DealFieldName.GEOPOINT],
      features: [
        FieldFeature.STANDARD,
        FieldFeature.NON_IMPORT,
        FieldFeature.NON_INTEGRATION,
        FieldFeature.NON_EXPORT_VIEW,
        FieldFeature.DEAL_ADDRESS_FIELD,
        FieldFeature.NON_ADDABLE_FORM_FIELD,
        FieldFeature.FILTERABLE,
        FieldFeature.FILTERABLE_ON_MAP,
        FieldFeature.GEO_POINT_FIELD,
      ],
      name: DealFieldName.GEOPOINT,
      valueGetter: (entity: unknown) => (entity as Deal).geoPoint?.coordinates,
    }),
    new FunnelField({
      displayName: displayNames[DealFieldName.FUNNEL],
      name: DealFieldName.FUNNEL,
      platformName: "funnelId",
    }),
    new LossReasonField({
      displayName: displayNames[DealFieldName.DEAL_LOSS_REASON],
      name: DealFieldName.DEAL_LOSS_REASON,
      platformName: "dealLossReasonId",
    }),
    {
      displayName: displayNames[DealFieldName.DEAL_LOSS_COMMENT],
      features: [
        FieldFeature.SORTABLE,
        FieldFeature.FILTERABLE,
        FieldFeature.FILTERABLE_ON_MAP,
        FieldFeature.NON_ADDABLE_FORM_FIELD,
        FieldFeature.HISTORY_FIELD,
        FieldFeature.DETAILS,
        FieldFeature.BULK_EDIT,
      ],
      importProperties: {
        name: "dealLossComment",
      },
      name: DealFieldName.DEAL_LOSS_COMMENT,
      type: FieldType.STRING,
    },
    new RottingDaysOutField({
      displayName: displayNames[DealFieldName.ROTTING_DAYS_OUT],
      name: DealFieldName.ROTTING_DAYS_OUT,
    }),
    {
      displayName: displayNames[DealFieldName.DAYS_IN_STAGE],
      features: [
        FieldFeature.SORTABLE,
        FieldFeature.FILTERABLE,
        FieldFeature.FILTERABLE_ON_MAP,
        FieldFeature.NON_IMPORT,
        FieldFeature.NON_INTEGRATION,
        FieldFeature.NON_ADDABLE_FORM_FIELD,
        FieldFeature.VISIBLE_BY_DEFAULT,
        FieldFeature.ENGAGEMENT,
        FieldFeature.DETAILS,
      ],
      name: DealFieldName.DAYS_IN_STAGE,
      type: FieldType.NUMBER,
    },
    new StageField({
      displayName: displayNames[DealFieldName.STAGE],
      name: DealFieldName.STAGE,
      platformName: "dealStageId",
    }),
    {
      displayName: displayNames[DealFieldName.STAGE_TYPE],
      features: [
        FieldFeature.FILTERABLE,
        FieldFeature.FILTERABLE_ON_MAP,
        FieldFeature.NON_EXPORT_VIEW,
        FieldFeature.NON_IMPORT,
        FieldFeature.NON_INTEGRATION,
        FieldFeature.NON_ADDABLE_FORM_FIELD,
        FieldFeature.NON_LIST_VIEW,
      ],
      name: DealFieldName.STAGE_TYPE,
      type: FieldType.STRING,
    },
    new DateTimeField({
      displayName: displayNames[DealFieldName.CREATED_AT],
      features: [
        FieldFeature.SORTABLE,
        FieldFeature.FILTERABLE,
        FieldFeature.FILTERABLE_ON_MAP,
        FieldFeature.NON_ADDABLE_FORM_FIELD,
        FieldFeature.NON_IMPORT,
        FieldFeature.NON_INTEGRATION,
      ],
      name: DealFieldName.CREATED_AT,
    }),
    new DateTimeField({
      displayName: displayNames[DealFieldName.UPDATED_AT],
      features: [
        FieldFeature.SORTABLE,
        FieldFeature.FILTERABLE,
        FieldFeature.FILTERABLE_ON_MAP,
        FieldFeature.NON_ADDABLE_FORM_FIELD,
        FieldFeature.NON_IMPORT,
        FieldFeature.NON_INTEGRATION,
      ],
      name: DealFieldName.UPDATED_AT,
    }),
    new TeamField({
      displayName: displayNames[DealFieldName.TEAM],
      name: DealFieldName.TEAM,
      platformFilterNames: ["teamId", "team.id"],
    }),
    new TerritoryField({
      displayName: displayNames[DealFieldName.TERRITORIES],
      name: DealFieldName.TERRITORIES,
    }),
    {
      displayName: displayNames[DealFieldName.ADDRESS],
      features: [
        FieldFeature.NON_IMPORT,
        FieldFeature.NON_MAP_VIEW,
        FieldFeature.NON_INTEGRATION,
        FieldFeature.NON_ADDABLE_FORM_FIELD,
        FieldFeature.DEAL_ADDRESS_FIELD,
      ],
      name: DealFieldName.ADDRESS,
      type: FieldType.STRING,
    },
    {
      displayName: displayNames[DealFieldName.CITY],
      features: [
        FieldFeature.NON_IMPORT,
        FieldFeature.NON_MAP_VIEW,
        FieldFeature.NON_INTEGRATION,
        FieldFeature.NON_ADDABLE_FORM_FIELD,
        FieldFeature.DEAL_ADDRESS_FIELD,
      ],
      name: DealFieldName.CITY,
      type: FieldType.STRING,
    },
    {
      displayName: displayNames[DealFieldName.REGION],
      features: [
        FieldFeature.NON_IMPORT,
        FieldFeature.NON_MAP_VIEW,
        FieldFeature.NON_INTEGRATION,
        FieldFeature.NON_ADDABLE_FORM_FIELD,
        FieldFeature.DEAL_ADDRESS_FIELD,
      ],
      name: DealFieldName.REGION,
      type: FieldType.STRING,
    },
    new CountryField({
      displayName: displayNames[DealFieldName.COUNTRY],
      exportColumnName: "geoAddress.country",
      features: [
        FieldFeature.NON_IMPORT,
        FieldFeature.NON_MAP_VIEW,
        FieldFeature.NON_INTEGRATION,
        FieldFeature.NON_ADDABLE_FORM_FIELD,
        FieldFeature.DEAL_ADDRESS_FIELD,
      ],
      importProperties: { required: false },
      name: DealFieldName.COUNTRY,
    }),
    {
      displayName: displayNames[DealFieldName.POSTAL_CODE],
      features: [
        FieldFeature.NON_IMPORT,
        FieldFeature.NON_MAP_VIEW,
        FieldFeature.NON_INTEGRATION,
        FieldFeature.NON_ADDABLE_FORM_FIELD,
        FieldFeature.DEAL_ADDRESS_FIELD,
      ],
      name: DealFieldName.POSTAL_CODE,
      type: FieldType.STRING,
    },
    {
      displayName: displayNames[DealFieldName.COMPANY_EMAIL],
      features: [
        FieldFeature.NON_IMPORT,
        FieldFeature.NON_INTEGRATION,
        FieldFeature.NON_LIST_VIEW,
        FieldFeature.NON_MAP_VIEW,
        FieldFeature.NON_ADDABLE_FORM_FIELD,
      ],
      name: DealFieldName.COMPANY_EMAIL,
      type: FieldType.STRING,
    },
    {
      displayName: displayNames[DealFieldName.COMPANY_IMPORT_ID],
      features: [
        FieldFeature.NON_IMPORT,
        FieldFeature.NON_INTEGRATION,
        FieldFeature.NON_LIST_VIEW,
        FieldFeature.NON_MAP_VIEW,
        FieldFeature.NON_ADDABLE_FORM_FIELD,
      ],
      name: DealFieldName.COMPANY_IMPORT_ID,
      type: FieldType.STRING,
    },
    {
      displayName: displayNames[DealFieldName.PERSON_EMAIL],
      features: [
        FieldFeature.NON_IMPORT,
        FieldFeature.NON_INTEGRATION,
        FieldFeature.NON_LIST_VIEW,
        FieldFeature.NON_MAP_VIEW,
        FieldFeature.NON_ADDABLE_FORM_FIELD,
      ],
      name: DealFieldName.PERSON_EMAIL,
      type: FieldType.STRING,
    },
    {
      displayName: displayNames[DealFieldName.PERSON_IMPORT_ID],
      features: [
        FieldFeature.NON_IMPORT,
        FieldFeature.NON_INTEGRATION,
        FieldFeature.NON_LIST_VIEW,
        FieldFeature.NON_MAP_VIEW,
        FieldFeature.NON_ADDABLE_FORM_FIELD,
      ],
      name: DealFieldName.PERSON_IMPORT_ID,
      type: FieldType.STRING,
    },
    new FrequencyDaysLeft({
      displayName: displayNames[DealFieldName.DAYS_LEFT_IN_FREQUENCY],
      name: DealFieldName.DAYS_LEFT_IN_FREQUENCY,
    }),
    // filter-only fields:
    new ViewAsField({
      displayName: displayNames[DealFieldName.VIEW_AS],
      name: DealFieldName.VIEW_AS,
    }),
    {
      displayName: displayNames[DealFieldName.QUICK_FILTER],
      features: [
        FieldFeature.NON_INTEGRATION,
        FieldFeature.NON_LIST_VIEW,
        FieldFeature.NON_MAP_VIEW,
        FieldFeature.NON_IMPORT,
        FieldFeature.NON_EXPORT_VIEW,
        FieldFeature.NON_ADDABLE_FORM_FIELD,
      ],
      name: DealFieldName.QUICK_FILTER,
      type: FieldType.STRING,
    },
  ].map(createField)
);

export default dealFieldModel;
